import React, { useMemo } from 'react';

import type { NumberLinePayload } from '../../core/types/payloads/number_line.types';
import { buildNumberLineLayout, NUMBER_LINE_WIDTH } from './numberLineLayout';

export interface NumberLineViewProps {
  payload: NumberLinePayload;
  answers?: Record<string, string>;
  interactive?: boolean;
  onAnswerChange?: (value: number, answer: string) => void;
}

function NumberBlankBox({
  value,
  answer,
  interactive,
  onAnswerChange,
}: {
  value: number;
  answer?: string;
  interactive?: boolean;
  onAnswerChange?: (value: number, answer: string) => void;
}) {
  if (interactive) {
    return (
      <input
        type="number"
        inputMode="numeric"
        aria-label={`Completa ${value}`}
        value={answer ?? ''}
        onChange={(event) => onAnswerChange?.(value, event.target.value)}
        className="number-line-blank h-9 w-full rounded-md border border-slate-300 bg-white text-center text-sm font-semibold text-slate-800 shadow-sm print:border-[#cbd5e1]"
      />
    );
  }

  return (
    <span
      className="number-line-blank inline-flex h-9 w-full min-w-[2.25rem] items-center justify-center rounded-md border border-slate-300 bg-white px-2 text-sm font-semibold text-slate-400 shadow-sm print:border-[#cbd5e1]"
      aria-label="Espacio para completar"
    >
      {answer?.trim() ? answer : ''}
    </span>
  );
}

export function NumberLineView({
  payload,
  answers = {},
  interactive = false,
  onAnswerChange,
}: NumberLineViewProps) {
  const instruction = String(payload.instruction || '').trim();
  const missingSet = useMemo(() => new Set(payload.missingNumbers), [payload.missingNumbers]);
  const layout = useMemo(
    () => buildNumberLineLayout(payload),
    [payload.start, payload.end, payload.step],
  );

  const svgHeight = layout.axisY + 56;

  return (
    <article className="number-line rounded-xl border border-slate-100 bg-white/80 p-6 print:border-[#ddd] print:bg-white">
      {instruction ? (
        <p className="number-line__instruction mb-4 text-sm font-medium text-slate-600 print:text-slate-700">
          {instruction}
        </p>
      ) : null}

      <div className="number-line__stage overflow-x-auto rounded-xl border border-slate-100 bg-slate-50/50 p-4 print:border-[#e2e8f0] print:bg-[#f8fafc]">
        <svg
          viewBox={`0 0 ${NUMBER_LINE_WIDTH} ${svgHeight}`}
          role="img"
          aria-label="Recta numérica"
          className="mx-auto block"
          style={{ width: NUMBER_LINE_WIDTH, maxWidth: '100%', height: 'auto' }}
        >
          <defs>
            <marker
              id="number-line-arrow"
              markerWidth="8"
              markerHeight="8"
              refX="6"
              refY="4"
              orient="auto"
            >
              <path d="M0,0 L8,4 L0,8 Z" fill="#64748b" />
            </marker>
          </defs>

          <line
            x1={layout.paddingX - 8}
            y1={layout.axisY}
            x2={NUMBER_LINE_WIDTH - layout.paddingX + 8}
            y2={layout.axisY}
            stroke="#64748b"
            strokeWidth="2"
            markerEnd="url(#number-line-arrow)"
          />

          {layout.positions.map(({ tick, x }) => (
            <g key={tick}>
              <line
                x1={x}
                y1={layout.axisY - 10}
                x2={x}
                y2={layout.axisY + 10}
                stroke="#94a3b8"
                strokeWidth="2"
              />
              {missingSet.has(tick) ? (
                <foreignObject
                  x={x - 22}
                  y={layout.axisY + 16}
                  width={44}
                  height={36}
                  requiredExtensions="http://www.w3.org/1999/xhtml"
                >
                  <div xmlns="http://www.w3.org/1999/xhtml">
                    <NumberBlankBox
                      value={tick}
                      answer={answers[String(tick)]}
                      interactive={interactive}
                      onAnswerChange={onAnswerChange}
                    />
                  </div>
                </foreignObject>
              ) : (
                <text
                  x={x}
                  y={layout.axisY + 34}
                  textAnchor="middle"
                  className="fill-slate-800 text-[15px] font-semibold"
                >
                  {tick}
                </text>
              )}
            </g>
          ))}
        </svg>
      </div>
    </article>
  );
}
